/* eslint-disable */
/*
 * Copyright (c) 2018 Wells Fargo, All rights reserved.
 *
 * This software is the confidential and proprietary information of Wells Fargo
 * bank. ('Confidential Information'). You shall not disclose such Confidential
 * Information and shall use it only in accordance with the terms of the license
 * agreement you entered into with WellsFargo.
 */

/* jslint browser: true */

import coreBootstrap from '@wellsfargo.sdk1/core-v3/dist/core-bootstrap';
import coreMessaging from '@wellsfargo.sdk1/core-v3/dist/core-messaging';
import parseUrl from '@wellsfargo.sdk1/core-v3/dist/core-url';

const userAgent =
    window && window.navigator && window.navigator.userAgent.toLowerCase();

const isIE = userAgent => !!userAgent.match(/Trident/i);

let conf;

const IFRAME_PATH = '/public/landing';
const IFRAME_INITIAL_TITLE = 'Wells Fargo Credit Card Application';

const handlers = {};

// -----
// REVEALED FUNCTIONS
// -----

/**
 * Initialize the SDK
 *
 * @param {object} [config] An optional configuration object
 * @return {function} An SDK proxy function that allows API access to the SDK instance
 * created by this initialize call.
 */

export const { display, show, hide, destroy } = coreBootstrap;

function handleError(errorMessage, config = {}) {
  let hasBeenLogged;
  const fullMessage = `CBCC SDK BOOTSTRAP ERROR: ${errorMessage}`;

  if (config.callbacks && config.callbacks.general && config.callbacks.general) {
    const { error, log } = config.callbacks.general;
    // Invoke general error callback if provided
    if (error && error instanceof Function) {
      error({ error_message: fullMessage});
      hasBeenLogged = true;
    }
    // Invoke general log callback if provided
    if (log && log instanceof Function) {
      log({ message: fullMessage });
      hasBeenLogged = true;
    }
    // log to the console if hasn't logged yet and if the configuration allows it
    if (!hasBeenLogged && console && config.consoleLoggingEnabled) {
      console.error(fullMessage);
    }
  }
  return false;
}

export const validateConfig = function(config) {
  if (!config) {
    return false;
  }

  if (!config.coBrand || typeof config.coBrand !== 'object') {
    return handleError('CoBrand object not found in external configuration object.')
  }

  if (!config.gatewayEntityId || typeof config.gatewayEntityId !== 'string') {
    return handleError('gatewayEntityId not found in external configuration object.')
  }

  const parsedApplicationsUrl = parseUrl(config.applicationsUrl);
  if (parsedApplicationsUrl.hostname !== 'localhost' &&
    parsedApplicationsUrl.hostname !== '10.0.2.2' &&
    parsedApplicationsUrl.hostname !== '127.0.0.1' &&
    parsedApplicationsUrl.protocol !== 'https:') {
      return handleError(
      'The applicationsUrl must use HTTPS or a local hostname' +
      ' (localhost, 10.0.2.2, or 127.0.0.1)', config);
  }

  // rewrite the URL (converts relative into absolute)
  config.applicationsUrl = parsedApplicationsUrl.fullURL;

  return true;
};

export const initialize = function(config) {
  if (config) {
    conf = config;
  } else {
    if (!getConfig()) {
      return false;
    }
  }

  const lang = document.getElementsByTagName('html')[0].getAttribute('lang');
  conf.language = lang;
  conf.parentDimensions = { height: window.innerHeight, width: window.innerWidth };


  if (!validateConfig(coreBootstrap.getConfig(conf))) {
    return false;
  }

  // if IE11 - don't initialize, display IE error screen
  if (isIE(userAgent)) {
    conf.callbacks.general.error({message: 'IE browser not supported'});
    const lang = conf.language.slice(0, 2) === 'es' ? 'es' : 'en';
    const isEsp = lang === 'es';

    const handleExit = () => {
      conf.callbacks.general.exit('Browser not supported screen');
      document.getElementById(conf.targetDivId).removeChild(errorContainer);
    }
    const errorContainer = document.createElement('div');
    errorContainer.style.background = '#fff';
    errorContainer.style.color = '#333';
    errorContainer.style.padding = '48px 24px';
    errorContainer.style.margin = 'auto 0';
    errorContainer.style.fontFamily = 'Arial, Helvetica, sans-serif';

    const headerText = document.createElement('h1');
    headerText.textContent = isEsp ? 'Navegador no compatible' : 'Unsupported Browser';
    headerText.style.fontSize = '20px';
    headerText.style.lineHeight = '28px';
    headerText.style.fontWeight =  'bold';
    errorContainer.appendChild(headerText);

    const paragraphText = document.createElement('p');
    paragraphText.textContent = isEsp
      ? 'Microsoft ya no ofrece apoyo en torno a Internet Explorer. Use un navegador diferente.'
      : 'Microsoft no longer supports Internet Explorer. Please use a different browser.';
    paragraphText.style.fontSize = '14px';
    paragraphText.style.margin = '0 0 8px  0';
    errorContainer.appendChild(paragraphText);

    const exitLink = document.createElement('button');
    exitLink.textContent = isEsp ? 'Salga' : 'Exit';
    exitLink.type ='button';
    exitLink.onclick = () => handleExit();
    exitLink.style.background = 'none';
    exitLink.style.border = 'none';
    exitLink.style.color = '#156bc1';
    exitLink.style.textDecoration = 'underline';
    exitLink.style.fontSize = '14px';
    exitLink.style.padding = '.5em 0';
    errorContainer.appendChild(exitLink);

    return document.getElementById(conf.targetDivId).appendChild(errorContainer);
  }

  // return a SDK proxy function that stores the iframeID and allows
  // easy API access to a specific iframe/SDK
  const sdkInstance = withCallbackOverride(coreBootstrap.initialize)(
    conf,
    IFRAME_PATH,
    IFRAME_INITIAL_TITLE
  );
  coreMessaging.addActionHandlers({
    onScrollTo: scrollToHandler({ sdkInstance }),
  });

  registerAdditionalHandlers(sdkInstance);
  return sdkInstance;
};

export const scrollToHandler = ({ sdkInstance }) => data => {
  const iframe = document.getElementById(sdkInstance.id);
  if (iframe) {
    const computedYPosition =
      data.top + iframe.offsetTop - window.innerHeight / 2;
      
    if (!Number.isNaN(computedYPosition)) {
      window.scrollTo(0, computedYPosition);
    }
  }
}

const getConfig = function() {
  if (!window.WF_AO_SDK_Config) {
    handleError('There is no WF_AO_SDK_Config object so the SDK cannot be loaded.');
    return false;
  }

  conf = window.WF_AO_SDK_Config;

  return true;
};

function withCallbackOverride(sdkInit) {
  return function overriden(conf, url, title) {
    // enforcing sdk destruction on exit.
    let sdkInstance;
    // exit callback check/insertion.
    if (!conf.callbacks) conf.callbacks = {};
    if (!conf.callbacks.general) conf.callbacks.general = {};
    const {exit, log} = conf.callbacks.general;

    conf.callbacks.general.exit = (...data) => {
      window.removeEventListener('resize', handlers[sdkInstance.id].resize)
      sdkInstance.destroy();
      if (typeof exit === 'function') exit(...data);
      if (typeof log === 'function') log({ message: 'Removing SDK from parent DOM.'});
    };
    sdkInstance = sdkInit(conf, url, title);
    return sdkInstance;
  }
}

function getParentDimensionsHandler(iframeId) {
  const { innerHeight: height, innerWidth: width } = window;
  coreBootstrap.sendMessage('setParentDimensions', {height, width}, iframeId);
}

function registerAdditionalHandlers(sdkInstance){
  if (!sdkInstance) {
    return;
  }

  {
     const resizeHandler = e => getParentDimensionsHandler(sdkInstance.id);
     if (handlers[sdkInstance.id]) {
       handlers[sdkInstance.id] = {
          ...handlers[sdkInstance.id],
          resize: resizeHandler
       }
     } else {
       handlers[sdkInstance.id] = { resize: resizeHandler }
     }
   }
   window.addEventListener('resize', handlers[sdkInstance.id].resize);
 }
